<template>
  <frontoffice-frame class="completion">
    <b-row v-if="isLoading" class="loader-section">
      <frontoffice-loader/>
    </b-row>

    <div v-else-if="!serverError">
      <page-title>
        <span>
          {{ $t('online.booking.title') }}
        </span>

        <button class="orange-white btn" @click="goBack()">
          {{ $t('online.offer.title') }}
        </button>
      </page-title>

      <div class="offline-research">
        <hotel-designation :name="formData.hotel.hotelName" :stars="formData.hotel.category"
                           :city="formData.hotel.city" class="col-12"
        />
        <part class="hotel row justify-content-center">
          <div class="col-12 completion-summary">
            <div class="row bg-light">
              <div class="col-6 col-lg-4 arrival">
                <span class="title">
                  {{ $t('online.booking.completion.hotel.arrival') }} :
                </span>

                <span class="date">
                  {{ formatDate(formData.dates.from) }}
                </span>
              </div>

              <div class="col-6 col-lg-4 departure">
                <span class="title">
                  {{ $t('online.booking.completion.hotel.departure') }} :
                </span>

                <span class="date">
                  {{ formatDate(formData.dates.to) }}
                </span>
              </div>

              <div class="col-12 col-lg-4 rooms">
                <span class="title">
                  {{ $tc('online.booking.completion.hotel.rooms', nbSingleRooms + nbDoubleRooms) }} :
                </span>

                <p v-if="nbSingleRooms" class="single">
                  <span>{{ nbSingleRooms }}</span>
                  <span>{{ $tc('online.booking.completion.hotel.single', nbSingleRooms) }}</span>
                </p>

                <p v-if="nbDoubleRooms" class="double"
                   :class="nbSingleRooms ? 'double-block' : 'double-inline'"
                >
                  <span>{{ nbDoubleRooms }}</span>
                  <span> {{ $tc('online.booking.completion.hotel.double', nbDoubleRooms) }}</span>
                </p>
              </div>
            </div>
          </div>

          <div :class="[ hotelMainPicture ? 'col-12 col-lg-9 col-xl-8 booking-sum-part' : 'col-lg-10 col-xl-8' ]">
            <div :class="hotelMainPicture ? 'booking-sum-section':'' ">
              <b-col class="price">
                <prices :booking="fakeBooking" show-title show-reminder />

                <div class="cancellation" :class="cancellationDetails.class">
                  {{ cancellationDetails.text }}
                </div>
              </b-col>
            </div>
          </div>

          <b-col v-if="hotelMainPicture"
                 :class="[ hotelMainPicture ? 'col-12 col-lg-3 col-xl-4 hotel-img' : 'img undisplay' ]"
          >
            <img :src="hotelMainPicture" alt="" />
          </b-col>
        </part>

        <div v-if="context === 'formation'" class="travellers-row">
          <part class="row travellers-formation"
                :title="$t('online.booking.completion.travellers.part')"
          >
            <div v-for="(value, type) in formData.bedrooms" :key="value.id"
                 v-if="value.amount" :class="getRoomTypeClass"
            >
              <traveller-formation-form v-for="n in value.amount" v-if="n"
                              :room-type="type" :index="n"
                              :description="value.travellers[0].roomInfos.description"
                              :allocation="travellersAllocation"
              />
            </div>
          </part>

          <div class="row" :style="{marginBottom: '2em', paddingLeft: '15px'}">
            <b-form-checkbox id="notify-travellers" name="notify-travellers"
                             v-model="formData.notifyTravellers"
            >
              {{ $t('_notifyTravellers') }}
            </b-form-checkbox>
          </div>

        </div>

        <div v-else class="travellers-row">
          <part class="row travellers"
                :title="$t('online.booking.completion.travellers.part')"
          >
            <div v-for="(value, type) in formData.bedrooms" :key="value.id"
                 v-if="value.amount" :class="getRoomTypeClass"
            >
              <traveller-form v-for="n in value.amount" v-if="n"
                              :room-type="type" :index="n" @traveller="onChangeTraveller"
                              :already-selected-travellers="alreadySelectedTravellers"
                              :description="value.travellers[0].roomInfos.description"
                              :allocation="travellersAllocation"
              />
            </div>
          </part>

          <div class="row" :style="{marginBottom: '2em', paddingLeft: '15px'}">
            <b-form-checkbox id="notify-travellers" name="notify-travellers"
                             v-model="formData.notifyTravellers"
            >
              {{ $t('_notifyTravellers') }}
            </b-form-checkbox>
          </div>

        </div>

        <b-row>
          <part class="col-12 col-lg-6 email-cc" :title="$t('_email_copies')">
            <email-copies v-model="formData.emailCopies" @add="addEmail" />
          </part>
        </b-row>

        <b-row>
          <part class="col-12 extra"
                :title="$t('online.booking.completion.extra.part')"

          >
            <textarea-input name="extra" v-model="formData.comment"
                            :placeholder="$t('_textarea.phrase.online')"
            />
          </part>
        </b-row>

        <b-row class="justify-content-center" v-if="formData.customFields.length">
          <part class=" col-12 refs"
                :title="$t('online.booking.completion.refs.part')"
          >
            <custom-input-controller :fields="formData.customFields"
                                     @input="onInputCustomFields"
            />
          </part>
        </b-row>
      </div>

      <div v-if="displayErrors" class="errors alert-danger">
        <div v-for="error in errors" :key="error.id" class="error">
          <span class="label">{{ getErrorLabelTranslation(error.field) | capitalise }} :</span>{{ error.message }}
        </div>
      </div>

      <div class="centered-button padTop-medium">
        <multi-manager-modal @input="formData.managers = $event" />

        <button class="btn orange-white" @click="checkManagersValidation">
          {{ $t('online.booking.btn.complete_booking') | capitalise }}
        </button>
      </div>
    </div>
  </frontoffice-frame>
</template>

<script>
import { countNbNights } from '@/utils/helpers'
import { fieldTypes } from '@/utils/fieldTypeCodes'
import formatter from '@/components/global/mixins/formatterMethods'
import CommonApi from '@/api/commonApi'
import FormSubmitter from '@/api/formSubmitter'
// Mixins
import errorsLabelsTranslationMixin from '@/components/frontoffice/mixins/errorsLabelsTranslation'
import travellersMixin from '@/components/frontoffice/mixins/travellers'
import bookingDeadlineMixin from '@/components/frontoffice/mixins/bookingDeadline'
// components
import frontofficeFrame from '@/components/frontoffice/frame'
import frontofficeLoader from '@/components/frontoffice/loader'
import pageTitle from '@/components/frontoffice/pageTitle'
import part from '@/components/frontoffice/part'
import hotelDesignation from '@/components/frontoffice/hotel/designation'
import textareaInput from '@/components/frontoffice/form/baseInput/textArea'
import radioInput from '@/components/frontoffice/form/baseInput/radio'
import customInputController from '@/components/frontoffice/form/customInput/controller'
import emailCopies from '@/components/frontoffice/form/emailCopies'
import backButton from '@/components/frontoffice/button/back'
import prices from '@/components/frontoffice/Booking/prices'
import multiManagerModal from '@/components/frontoffice/Booking/modal/multiManager'

export default {
  components: {
    frontofficeFrame,
    frontofficeLoader,
    pageTitle,
    part,
    textareaInput,
    radioInput,
    customInputController,
    hotelDesignation,
    emailCopies,
    backButton,
    prices,
    multiManagerModal
  },

  mixins: [
    formatter,
    travellersMixin,
    errorsLabelsTranslationMixin,
    bookingDeadlineMixin
  ],

  data () {
    return {
      isLoading: true,
      serverError: false,
      commonApi: new CommonApi(),
      formData: {
        dates: {
          from: this.$store.getters['research/from'],
          to: this.$store.getters['research/to']
        },
        destination: this.$store.state.research.destination,
        customFields: null,
        bedrooms: {
          single: {
            amount: 0,
            travellers: []
          },
          double: {
            amount: 0,
            travellers: []
          }
        },
        comment: null,
        rooms: null,
        hotel: null,
        nbNights: null,
        price: {
          overall: 0
        },
        cancellationDeadline: null,
        residenceTax: 0,
        variableTax: 0,
        cancellationPolicy: '',
        roomOffers: this.$store.state.onlineBooking.selectedRooms,
        emailCopies: [''],
        managers: [],
        notifyTravellers: this.$store.getters['security/sendVoucherToTravellers']
      },
      hotelKey: this.$store.state.onlineBooking.hotelKey,
      bedroomsIndexes: {
        single: 0,
        double: 0
      },
      displayErrors: false,
      errors: [],
      cancellationDetails: {},
      fakeBooking: {},
      travellersAllocation: null,
      context: 'online'
    }
  },

  created () {
    this.context = this.$store.getters['research/context']

    if (this.$route.params.action === 'edit') {
      this.formData = this.$store.state.onlineBooking.current.formData
      this.fakeBooking = this.$store.state.onlineBooking.current.fakeBooking
      this.setFees()
      this.isLoading = false
    } else {
      let send = {
        hotelKey: this.$store.state.onlineBooking.hotelKey,
        startDate: this.$store.getters['research/from'],
        endDate: this.$store.getters['research/to'],
        singleRooms: this.$store.getters['research/single'],
        doubleRooms: this.$store.getters['research/double'],
        selectedOffers: this.$store.state.onlineBooking.selectedRooms
      }

      this.isLoading = true
      this.commonApi.getSelectedOffers(send)
        .then(response => {
          this.setFormData(response.data.hotel, response.data.availabilities)
        })
        .then(() => {
          this.setCompanyInputFields()
        })
        .then(() => {
          this.setFees()
        })
        .then(() => {
          this.cancellationDetails = this.getNearestDeadlineDetails(
            this.formData.roomOffers.single.concat(this.formData.roomOffers.double)
          )

          this.formData.cancellationDeadline = this.cancellationDetails.date
        })
        .catch((e) => {
          this.isLoading = false
          this.serverError = true
        })
    }

    this.setTravellersAllocation()
  },

  methods: {
    goBack () {
      this.$router.push({ name: 'online_offers', params: { hotelKey: this.hotelKey } })
    },

    setCompanyInputFields () {
      this.commonApi.companyInputFields().then(response => {
        let data = response.data.data
        let ret = []

        // Set default values
        for (let i in data) {
          if (data.hasOwnProperty(i)) {
            let field = data[i]
            switch (field.type) {
              case fieldTypes['NUMERIC']: field.value = 0
                break
              case fieldTypes['TEXT']: field.value = ''
                break
              case fieldTypes['EMAIL']: field.value = ''
                break
              case fieldTypes['PHONE']: field.value = ''
                break
              case fieldTypes['DROP_DOWN_MENU']: field.value = null
                break
              case fieldTypes['BOOLEAN']:
                for (let i in field.options) {
                  if (field.options.hasOwnProperty(i)) {
                    let option = field.options[i]
                    option.selected = i === 0
                  }
                }
                break
            }

            ret.push(field)
          }
        }

        this.formData.customFields = ret
        this.isLoading = false
      })
    },

    setFormData (hotel, offers) {
      this.formData.roomOffers = offers
      let rooms = []
      let storeRooms = this.$store.state.research.bedrooms

      for (let type in storeRooms) {
        if (storeRooms.hasOwnProperty(type)) {
          for (let room of storeRooms[type].travellers) {
            let tempRoom = {
              type: type,
              _traveller1: room[1]
            }

            if (type === 'double') {
              tempRoom['_traveller2'] = room[2]
              tempRoom.bedType = room.bedType
            }

            rooms.push(tempRoom)
          }
        }
      }

      this.initFormDataRooms(this.nbSingleRooms, this.nbDoubleRooms, rooms || null)

      this.formData.hotel = hotel
      this.formData.rooms = this.nbRooms
      this.formData.nbNights = this.nbNights

      let roomOffers = this.formData.roomOffers.single.concat(this.formData.roomOffers.double)
      let fb = {
        price: 0,
        breakfast_included: false,
        breakfast_price: 0,
        residence_tax: 0,
        variable_tax: 0,
        arrival_date: this.formData.dates.from,
        departure_date: this.formData.dates.to
      }

      for (let i in roomOffers) {
        if (roomOffers.hasOwnProperty(i)) {
          let offer = roomOffers[i].offer
          fb.price += parseFloat(offer.totalPriceInclusiveCustomer.amount)
          fb.breakfast_included = offer.breakfastType === 'inclusive'
          fb.breakfast_price = +offer.averageBreakfastPriceCustomer.amount

          // taxes
          for (let j in offer.taxesAndFees.taxAndFeeDetails) {
            if (offer.taxesAndFees.taxAndFeeDetails.hasOwnProperty(j)) {
              let tax = offer.taxesAndFees.taxAndFeeDetails[j]

              if (tax.taxAndFeeType === 'visitorTax' &&
                !tax.inclusive &&
                tax.amountPerChargeUnitCustomer &&
                tax.amountPerChargeUnitCustomer.amount
              ) {
                fb.residence_tax = +tax.amountPerChargeUnitCustomer.amount
                this.formData.residenceTax = +tax.amountPerChargeUnitCustomer.amount
              }
            }
          }

          // cancellation policy
          if (offer.rates) {
            this.formData.cancellationPolicy = offer.rates[0].priceComment
          }
        }
      }

      if (fb.breakfast_price) fb.price += +fb.breakfast_price * this.nbNights * this.nbTravellers

      if (fb.residence_tax) fb.price += +fb.residence_tax * this.nbNights * this.nbTravellers

      this.fakeBooking = fb
      this.formData.price.overall = fb.price
      this.formData.emailCopies = [this.$store.getters['security/email2'] || '']
    },

    onInputCustomFields (newVal) {
      this.formData.customFields.map(function (customField) {
        if (customField.id === newVal.id) {
          customField.value = newVal.value
        }
      })
    },

    checkManagersValidation () {
      // TODO : A supprimer lorsque la politique de voyage non conforme sera gérée en Online (revert commit lié au ticket 6789)
      // this.completeRequest()

      // TODO : A décommenter lorsque la politique de voyage non conforme sera gérée en Online (revert commit lié au ticket 6789)
      // let preval = this.$store.getters['security/company'].managerPreVal
      // let postval = this.$store.getters['security/company'].managerPostVal
      // if (!preval && !postval) {
      //   this.completeRequest()
      // } else if (!this.$store.getters['security/company'].multiManagerEnabled) {
      //   this.completeRequest()
      // } else if (this.formData.managers.length) {
      //   this.completeRequest()
      // } else {
      //   this.$bvModal.show('multi-manager-modal')
      // }
      if (!this.$store.getters['security/company'].multiManagerEnabled || this.formData.managers.length) { this.completeRequest() } else { this.$bvModal.show('multi-manager-modal') }
    },

    completeRequest () {
      let submitter = new FormSubmitter('submit_online_booking')

      this.formData.hotel.hrs_ref = this.hotelKey

      this.formData['context'] = this.context

      submitter.submitRequest(this.formData)
        .then(response => {
          if (response['data']['success']) {
            this.dealSuccess(response['data']['data'])
          } else {
            this.dealFailure(response['data']['errors'])
          }
        })
    },

    dealSuccess (response) {
      this.displayErrors = false
      this.$store.commit('onlineBooking/setUnfinishedId', response.bookingId)
      this.$store.commit('onlineBooking/setCurrent', {
        formData: this.formData,
        fakeBooking: this.fakeBooking
      })
      this.$router.push({ name: 'booking_summary' })
    },

    dealFailure (response) {
      this.errors = []
      let managedErrors = []

      for (let fields in response) {
        if (response.hasOwnProperty(fields)) {
          let code = parseInt(response[fields])
          let message = this.$i18n.te('error.' + code) ? this.$i18n.t('error.' + code) : response[fields]
          let listFields = []

          if (fields.indexOf(',') !== -1) {
            listFields = fields.split(',')
          } else {
            listFields = [fields]
          }

          // Special treatment for offers
          if (fields === 'offers') {
            message = 'Les offres ne sont plus disponibles'
            listFields = ['bedrooms']
          }

          for (let field of listFields) {
            if (managedErrors.indexOf(field) === -1) {
              this.errors.push({
                field: field,
                message: message
              })
            }
          }
        }
      }

      this.displayErrors = true
    },

    addEmail () {
      this.formData.emailCopies.push('')
    },

    setTravellersAllocation () {
      let send = true

      for (let type in this.formData.bedrooms) {
        if (this.formData.bedrooms.hasOwnProperty(type)) {
          for (let room of this.formData.bedrooms[type].travellers) {
            for (let key of Object.keys(room)) {
              if (room[key] === null) send = false
            }
          }
        }
      }

      if (send) this.travellersAllocation = this.formData.bedrooms
    },

    setFees () {
      let fees = this.$store.getters['security/company'].bookingFees

      if (fees !== null) {
        this.fakeBooking.fees_global_label = fees.global.label
        this.fakeBooking.fees_global = fees.global.online
        this.fakeBooking.fees_per_room_label = fees.perRoom.label
        this.fakeBooking.fees_per_room = fees.perRoom.online
        this.fakeBooking.price +=
          (fees.global.online || 0) +
          this.nbRooms * (fees.perRoom.online || 0)
      }
    }
  },

  computed: {
    hotelMainPicture () {
      let picture = ''

      for (let i in this.formData.hotel.media) {
        if (this.formData.hotel.media.hasOwnProperty(i)) {
          let media = this.formData.hotel.media[i]
          if (media.mainMedia) {
            picture = media.mediaURL
          }
        }
      }

      return picture //
    },

    nbRooms () {
      return this.nbSingleRooms + this.nbDoubleRooms
    },

    nbSingleRooms () {
      return this.formData.roomOffers.single.length
    },

    nbDoubleRooms () {
      return this.formData.roomOffers.double.length
    },

    nbTravellers () {
      return this.nbSingleRooms + this.nbDoubleRooms * 2
    },

    nbNights () {
      return countNbNights(this.formData.dates.from, this.formData.dates.to)
    }
  }
}
</script>
